// src/pages/DealersStockView/components/DealersStockFilter.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import DealerDropdown from '../../../components/common/DealerDropdown';
import DealerAddressDropdown from '../../../components/common/DealerAddressDropdown';
import { useLanguage } from '../../../contexts/LanguageContext';

const DealersStockFilter = ({ initiallyOpen = true, onSelect, onReset, onApplyFilters, showPartCodeOnly = false }) => {
    const { th } = useLanguage();

    const [filterOpen, setFilterOpen] = useState(initiallyOpen);

    const [dealer, setDealer] = useState(null);
    const [address, setAddress] = useState(null);
    const [partCode, setPartCode] = useState('');
    const [partDesc, setPartDesc] = useState('');
    const [block, setBlock] = useState('');
    const [shelf, setShelf] = useState('');
    const [sequence, setSequence] = useState('');
    const [eyes, setEyes] = useState('');

    useEffect(() => {
        setAddress(null);
    }, [dealer?.id, dealer?.dealerId, dealer?.erpDealerId, dealer?.ErpDealerId]);

    const handleFilterToggle = () => setFilterOpen((prev) => !prev);

    const handleApply = useCallback(() => {
        if (showPartCodeOnly) {
            onApplyFilters?.(partCode, partDesc, block, shelf, sequence, eyes);
        } else {
            if (!dealer || !address) return;
            onSelect?.({ dealer, address, partCode, partDesc });
            onApplyFilters?.(partCode, partDesc, block, shelf, sequence, eyes);
        }
    }, [dealer, address, partCode, partDesc, block, shelf, sequence, eyes, onSelect, onApplyFilters, showPartCodeOnly]);

    const handleReset = useCallback(() => {
        setDealer(null);
        setAddress(null);
        setPartCode('');
        setPartDesc('');
        setBlock('');
        setShelf('');
        setSequence('');
        setEyes('');
        onReset?.();
        onApplyFilters?.('', '', '', '', '', '');
    }, [onReset, onApplyFilters]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleApply();
    };

    return (
        <div className="generic-table-filter" onKeyDown={handleKeyDown}>
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button icon="pi pi-filter" label={th('filter.advancedFilter')} onClick={handleFilterToggle} outlined={!filterOpen} className={filterOpen ? '' : ''} />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('filter.advancedFilters')}</div>

                    <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                        {!showPartCodeOnly && (
                            <>
                                <div className="flex-auto" style={{ minWidth: 240, maxWidth: 320 }}>
                                    <label htmlFor="dealer" className="block mb-1 text-sm">
                                        {th('table.dealerName')}
                                    </label>
                                    <DealerDropdown value={dealer} onChange={setDealer} className="w-full" placeholder={th('warrantyReports.filter.dealerPlaceholder')} rowCount={20} />
                                </div>

                                <div className="flex-auto" style={{ minWidth: 240, maxWidth: 360 }}>
                                    <label htmlFor="dealerAddress" className="block mb-1 text-sm">
                                        {th('table.addressName')}
                                    </label>
                                    <DealerAddressDropdown
                                        dealerId={dealer?.id ?? dealer?.dealerId ?? dealer?.erpDealerId ?? dealer?.ErpDealerId}
                                        value={address}
                                        onChange={setAddress}
                                        className="w-full"
                                        placeholder={th('customerAddress.address')}
                                        disabled={!dealer}
                                    />
                                </div>
                            </>
                        )}

                        <div className="flex-auto" style={{ minWidth: 200, maxWidth: 280 }}>
                            <label htmlFor="partCode" className="block mb-1 text-sm">
                                {th('product.code')}
                            </label>
                            <InputText
                                id="partCode"
                                value={partCode}
                                onChange={(e) => setPartCode(e.target.value)}
                                className="w-full"
                                placeholder={th('parts.searchPartCode')}
                            />
                        </div>

                        <div className="flex-auto" style={{ minWidth: 200, maxWidth: 280 }}>
                            <label htmlFor="partDesc" className="block mb-1 text-sm">
                                {th('product.description') || 'Parça Açıklaması'}
                            </label>
                            <InputText
                                id="partDesc"
                                value={partDesc}
                                onChange={(e) => setPartDesc(e.target.value)}
                                className="w-full"
                                placeholder={th('parts.searchPartDesc') || 'Parça açıklaması ara'}
                            />
                        </div>

                        <div className="flex-auto" style={{ minWidth: 200, maxWidth: 280 }}>
                            <label htmlFor="block" className="block mb-1 text-sm">
                                {th('dealersStockView.stock.block')}
                            </label>
                            <InputText
                                id="block"
                                value={block}
                                onChange={(e) => setBlock(e.target.value)}
                                className="w-full"
                                placeholder={th('dealersStockView.stock.block')}
                            />
                        </div>

                        <div className="flex-auto" style={{ minWidth: 200, maxWidth: 280 }}>
                            <label htmlFor="shelf" className="block mb-1 text-sm">
                                {th('dealersStockView.stock.shelf')}
                            </label>
                            <InputText
                                id="shelf"
                                value={shelf}
                                onChange={(e) => setShelf(e.target.value)}
                                className="w-full"
                                placeholder={th('dealersStockView.stock.shelf')}
                            />
                        </div>

                        <div className="flex-auto" style={{ minWidth: 200, maxWidth: 280 }}>
                            <label htmlFor="sequence" className="block mb-1 text-sm">
                                {th('dealersStockView.stock.sequence')}
                            </label>
                            <InputText
                                id="sequence"
                                value={sequence}
                                onChange={(e) => setSequence(e.target.value)}
                                className="w-full"
                                placeholder={th('dealersStockView.stock.sequence')}
                            />
                        </div>

                        <div className="flex-auto" style={{ minWidth: 200, maxWidth: 280 }}>
                            <label htmlFor="eyes" className="block mb-1 text-sm">
                                {th('dealersStockView.stock.eyes')}
                            </label>
                            <InputText
                                id="eyes"
                                value={eyes}
                                onChange={(e) => setEyes(e.target.value)}
                                className="w-full"
                                placeholder={th('dealersStockView.stock.eyes')}
                            />
                        </div>
                    </div>

                    <div className="flex justify-content-start mt-3">
                        <Button label={th('filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={handleReset} />
                        <Button label={th('filter.apply')} icon="pi pi-check" onClick={handleApply} disabled={!showPartCodeOnly && (!dealer || !address)} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default DealersStockFilter;
